import { useForm } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import type { InvestorDetail, InvestorRelationship } from '@/types/investor'

interface Props {
  investor: InvestorDetail
  relationships: InvestorRelationship[]
}

const RELATIONSHIP_LABELS: Record<InvestorRelationship, string> = {
  family: 'Family',
  business_partner: 'Business Partner',
  fund_investor: 'Fund Investor',
  other: 'Other',
}

export default function EditInvestor({ investor, relationships }: Props) {
  const { data, setData, patch, processing, errors } = useForm({
    name: investor.name,
    email: investor.email || '',
    phone: investor.phone || '',
    relationship: investor.relationship || '',
    total_capital_committed: investor.total_capital_committed.toString(),
    notes: investor.notes || '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/investors/${investor.id}`)
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-1 text-sm text-gray-400">
          <a href="/investors" className="hover:text-gray-600">Investors</a>
          <span>/</span>
          <a href={`/investors/${investor.id}`} className="hover:text-gray-600">{investor.name}</a>
          <span>/</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Edit Investor</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-900">Basic Info</h2>

            <Field label="Name" error={errors.name} required>
              <input
                type="text"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                className={inputClass(errors.name)}
              />
            </Field>

            <Field label="Relationship" error={errors.relationship}>
              <select
                value={data.relationship}
                onChange={(e) => setData('relationship', e.target.value)}
                className={inputClass(errors.relationship)}
              >
                <option value="">— Select —</option>
                {relationships.map((r) => (
                  <option key={r} value={r}>
                    {RELATIONSHIP_LABELS[r]}
                  </option>
                ))}
              </select>
            </Field>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Email" error={errors.email}>
                <input
                  type="email"
                  value={data.email}
                  onChange={(e) => setData('email', e.target.value)}
                  className={inputClass(errors.email)}
                />
              </Field>
              <Field label="Phone" error={errors.phone}>
                <input
                  type="tel"
                  value={data.phone}
                  onChange={(e) => setData('phone', e.target.value)}
                  className={inputClass(errors.phone)}
                />
              </Field>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-900">Capital</h2>
            <Field label="Total Capital Committed" error={errors.total_capital_committed}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={data.total_capital_committed}
                  onChange={(e) => setData('total_capital_committed', e.target.value)}
                  className={`${inputClass(errors.total_capital_committed)} pl-7`}
                />
              </div>
            </Field>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
            <Field label="Notes" error={errors.notes}>
              <textarea
                value={data.notes}
                onChange={(e) => setData('notes', e.target.value)}
                rows={3}
                className={inputClass(errors.notes)}
              />
            </Field>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={processing}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {processing ? 'Saving...' : 'Save Changes'}
            </button>
            <a
              href={`/investors/${investor.id}`}
              className="px-6 py-2.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </a>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}

function Field({
  label,
  error,
  required,
  children,
}: {
  label: string
  error?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  )
}

function inputClass(error?: string) {
  return `w-full py-2.5 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
    error ? 'border-red-300 bg-red-50' : 'border-gray-300'
  }`
}
